import { Browser } from "@capacitor/browser";
import { shutdown } from "@intercom/messenger-js-sdk";
import { useQuery } from "@tanstack/react-query";
import { useHistory } from "react-router";
import {
  AppBuildVersionInfo,
  Breakpoints,
  Colors,
  IconBlockGroup,
  useFeatureFlags,
} from "swing-components";

import { ContentNavigationItem, ContentSingleColumn, useAuth } from "~components";
import {
  createDocumentsUrl,
  createHelpUrl,
  createLocationSettingsUrl,
  createMyDocumentsUrl,
  createNotificationsUrl,
  createPaymentUrl,
  createPermitURL,
  createReferralProgramUrl,
  createSkillBuilderTasksURL,
  createTimesheetUrl,
  generateWeekId,
} from "~pages";
import { BRANCH_ENTRY_URL, BUILD_SHA, BUILD_VER, GET, translate } from "~utils";

import { ScorePage } from "../../ScoreTemplates/ScorePage";

/**
 * Page | More Page
 *
 * The More page for a Sub that has completed onboarding.
 */
export function MorePage() {
  /*** Hooks ***/
  const { signOut, userInfo } = useAuth();
  const { flags } = useFeatureFlags();
  const history = useHistory();

  /*** Query ***/
  // Prefetch location settings data when More component mounts
  useQuery({
    queryKey: ["fetchLocationSettings", undefined],
    queryFn: () => GET("/api/sub/location"),
    // Only prefetch if user has access to location settings and geocode linkage is disabled
    enabled:
      !userInfo.restrictions?.includes("RESTRICTION_CANNOT_VIEW_LOCATIONS") &&
      !flags?.geocodeLinkage,
    // Keep the data in cache for 5 minutes
    staleTime: 5 * 60 * 1000,
  });

  /*** Render ***/
  return (
    <ScorePage title={translate("PAGE_TITLE_MORE")}>
      <ContentSingleColumn>
        <div
          css={{
            width: "100%",
            padding: "16px",
            [Breakpoints.mediumDeviceAndUp]: {
              padding: "0px 16px 16px",
            },
          }}
        >
          <div css={{ marginBottom: "24px" }}>
            {/* Settings */}
            <IconBlockGroup title="Settings">
              {/* Notifications */}
              <ContentNavigationItem
                navigationUrl={createNotificationsUrl()}
                icon={{ name: "Notifications" }}
                label={translate("MORE_NOTIFICATIONS")}
                navigationIcon="Chevron Right"
                hasWhiteBackground={false}
              />

              {/* Payment */}
              {!userInfo.isHourly &&
                !userInfo.restrictions?.includes("RESTRICTION_CANNOT_VIEW_PAY") && (
                  <ContentNavigationItem
                    icon={{ name: "Monetization" }}
                    label={translate("PAGE_TITLE_PAYMENT")}
                    navigationUrl={createPaymentUrl()}
                    navigationIcon="Chevron Right"
                    hasWhiteBackground={false}
                  />
                )}

              {/* My Documents */}
              {!userInfo.restrictions?.includes("RESTRICTION_CANNOT_VIEW_DOCUMENTS") && (
                <ContentNavigationItem
                  icon={{ name: "Note" }}
                  label={translate("SETTINGS_DOCUMENTS")}
                  navigationIcon="Chevron Right"
                  navigationUrl={
                    flags.myDocumentsV2 ? createDocumentsUrl() : createMyDocumentsUrl()
                  }
                  hasWhiteBackground={false}
                />
              )}
              {/* Locations Settings */}
              {!userInfo.restrictions?.includes("RESTRICTION_CANNOT_VIEW_LOCATIONS") &&
                !flags?.geocodeLinkage && (
                  <ContentNavigationItem
                    icon={{ name: "Location" }}
                    label={translate("MORE_LOCATION_SETTINGS")}
                    navigationUrl={createLocationSettingsUrl()}
                    hasDivider={false}
                    navigationIcon="Chevron Right"
                    hasWhiteBackground={false}
                  />
                )}
            </IconBlockGroup>
          </div>

          <div css={{ marginBottom: "24px" }}>
            {/* Resources */}
            <IconBlockGroup title="Resources">
              {/* UKG */}
              {userInfo.isHourly &&
                !userInfo.restrictions?.includes("RESTRICTION_CANNOT_VIEW_PAY") && (
                  <ContentNavigationItem
                    onClick={() => {
                      Browser.open({ url: "https://swingeduc.ukg.net/" });
                    }}
                    icon={{ name: "UKG" }}
                    label={translate("UKG_LINK_LABEL")}
                    navigationIcon="External Link"
                    hasWhiteBackground={false}
                  />
                )}

              {/* Timesheet */}
              {flags.swingSubsTimesheet &&
                userInfo.isHourly &&
                !userInfo.restrictions?.includes("RESTRICTION_CANNOT_VIEW_PAY") && (
                  <ContentNavigationItem
                    navigationUrl={createTimesheetUrl(generateWeekId())}
                    icon={{ name: "Punch Clock" }}
                    label="Timesheet"
                    navigationIcon="Chevron Right"
                    hasWhiteBackground={false}
                  />
                )}

              {userInfo?.branchStatus === "ACTIVATED" && (
                <ContentNavigationItem
                  icon={{ name: "Branch" }}
                  label={translate("BRANCH_LINK_LABEL")}
                  navigationIcon="External Link"
                  hasWhiteBackground={false}
                  onClick={() => {
                    Browser.open({ url: BRANCH_ENTRY_URL });
                  }}
                />
              )}

              {/* Skill Builder Program */}
              <ContentNavigationItem
                navigationUrl={createSkillBuilderTasksURL("swing")}
                icon={{ name: "Lesson" }}
                label="Skill Builder"
                navigationIcon="Chevron Right"
                hasWhiteBackground={false}
              />

              {/* Swing Cert Program */}
              {userInfo.canAccessPermitTask && (
                <ContentNavigationItem
                  navigationUrl={createPermitURL()}
                  icon={
                    userInfo.hasCredentialViaSwingCert
                      ? { name: "Check Circle", color: Colors.green500 }
                      : { name: "Permit" }
                  }
                  label={translate("PERMIT_PAGE_TITLE")}
                  navigationIcon="Chevron Right"
                  hasWhiteBackground={false}
                />
              )}

              {/* SubPlan.ai*/}
              <ContentNavigationItem
                icon={{ name: "Assignment" }}
                label={translate("MORE_SUB_PLAN_AI")}
                navigationIcon="External Link"
                hasWhiteBackground={false}
                onClick={() => Browser.open({ url: "https://lpgenreactnogate.pages.dev" })}
              />

              {/* Referral Program */}
              {!userInfo.restrictions?.includes("RESTRICTION_CANNOT_VIEW_REFERRAL") && (
                <div css={{ [Breakpoints.mediumDeviceAndUp]: { display: "none" } }}>
                  <ContentNavigationItem
                    navigationUrl={createReferralProgramUrl()}
                    icon={{ name: "Group" }}
                    label={translate("MORE_REFERRAL_PROGRAM")}
                    navigationIcon="Chevron Right"
                    hasWhiteBackground={false}
                  />
                </div>
              )}

              {/* Help */}
              <div css={{ [Breakpoints.mediumDeviceAndUp]: { display: "none" } }}>
                <ContentNavigationItem
                  navigationUrl={createHelpUrl()}
                  icon={{ name: "Help" }}
                  label={translate("MORE_HELP")}
                  navigationIcon="Chevron Right"
                  hasWhiteBackground={false}
                />
              </div>
            </IconBlockGroup>
          </div>
          {/* Conditionally hide Help and Log Out on desktop */}
          <div css={{ [Breakpoints.mediumDeviceAndUp]: { display: "none" } }}>
            <div css={{ marginBottom: "24px" }}>
              <IconBlockGroup>
                <ContentNavigationItem
                  icon={{ name: "Logout", color: Colors.red500 }}
                  label={translate("MORE_LOGOUT")}
                  onClick={() => {
                    // shutdown will be a noop if Intercom is not enabled
                    shutdown();
                    signOut({ history });
                  }}
                  hasWhiteBackground={false}
                />
              </IconBlockGroup>
            </div>
          </div>
          <AppBuildVersionInfo version={BUILD_VER} build={BUILD_SHA} />
        </div>
      </ContentSingleColumn>
    </ScorePage>
  );
}
